/**
 * Hemming module — opens the hem panel, manages length selection,
 * and runs the terms-fork modal flow.
 *
 * Mounts a `window.hemming` surface (handleSizeChange / openHemTermsFork
 * / closeHemPanel) for `<add-item-form>` to call as it processes form
 * state and submit. When `<add-item-form>` evolves further we can swap
 * the surface for direct ESM imports there too — no other consumers.
 *
 * Source path: theme/js/modules/hemming.esm.js
 * Built name : ./hemming.js
 *
 * AI: Entirely written by AI
 * Date: 2026-05-05
 * Model: Claude Opus 4.7
 *
 * [Hemming]
 */

import { log } from './log.js';
import { appGet, safeAppCall } from './app-get.js';
import { push as notifierPush, dismiss as notifierDismiss } from './notifier.js';
import { unwrapJQuery } from './dom.js';
import {
  toggleFormEnabled,
  renderFormError,
  HAS_VARIANT_CLASS,
} from './form-state.js';
import { fetchSection } from './sections.js';

const triggerSelector = '.js-add-hemming';
const triggerWrapSelector = '.js-hemming-trigger-wrap';
const hemmingWrapSelector = '.js-hemming-wrap';
const removeTriggerSelector = '.js-remove-hemming';
const optionWrapperSelector = '.js-hemming-option-wrap';
const hemLengthSelector = 'select#select-hem-length';
const hemPropertySelector = 'input[name="properties[Hem]"]';
const hemTermsPropertySelector = 'input[name="properties[_Hem Terms Accepted]"]';
const validateHemCheckboxSelector = 'input[name="accept_hemming_terms"]';
const acceptTermsSelector = '.js-accept-hem';
const declineTermsSelector = '.js-decline-hem';
const hemmingOptionRowSelector = '.js-hemming-option-row';
const appendedClass = 'appended';
const openOnAdd = true;
const disallowPlaceholder = true;

function getAddItemFormSelector() {
  return appGet('dom.product.addItemFormSelector', '.js-add-item-form');
}

function ga(args) {
  safeAppCall('ga', 'send', 'event', args);
}

function findClosestForm(el) {
  return el.closest(getAddItemFormSelector());
}

// ─── Open / close panel ───────────────────────────────────────────────────

// openHemPanel stays SYNCHRONOUS: the chain ends in handleSizeChange's
// `hemSelect.showPicker()`, which requires transient user activation. An
// async fetch round-trip can outlast the activation window (especially on
// WebKit), making the picker silently fail. The panel snippet itself is
// small and only loads on hemming-eligible PDPs (rendered inside
// add-item-form.liquid behind `{% if canHem %}`), so the inline
// `<template>` placement has limited blast radius. See the plan's
// hemming-revision addendum.
function openHemPanel(triggerEl) {
  const wrap = triggerEl.closest(hemmingWrapSelector);
  const optionWrapper = wrap && wrap.querySelector(optionWrapperSelector);
  const form = optionWrapper && findClosestForm(optionWrapper);
  if (!form || !optionWrapper) return;

  // Hem length depends on size (handleSizeChange filters options based
  // on currentSize). Without a variant, opening the panel makes no
  // sense — surface the same "Please select a size." error the form
  // uses on submit.
  if (!form.classList.contains(HAS_VARIANT_CLASS)) {
    renderFormError(form, appGet('i18n.product.error.size', 'Please select a size.'));
    return;
  }

  // Defensive: don't double-mount the panel if a previous open path is
  // still in-DOM (e.g. user clicked then immediately clicked again on a
  // very slow browser frame).
  if (optionWrapper.querySelector(hemmingOptionRowSelector)) return;

  const tpl = wrap.querySelector('.js-hemming-panel-template');
  if (!tpl || !tpl.content) return;
  optionWrapper.appendChild(tpl.content.cloneNode(true));
  optionWrapper
    .querySelectorAll(hemmingOptionRowSelector)
    .forEach((row) => row.classList.add(appendedClass));

  // The hemming wrap mounted into the form has its `change` event
  // routed by <add-item-form>'s `_onChange`, which re-runs variant
  // resolution and dispatches `variant:change`. No bridge needed.
  form.dispatchEvent(new Event('change', { bubbles: true }));

  const triggerWrap = triggerEl.closest(triggerWrapSelector);
  if (triggerWrap) triggerWrap.style.display = 'none';
}

export function closeHemPanel() {
  const formEl = document.querySelector(getAddItemFormSelector());
  if (!formEl) return;
  const optionWrapper = formEl.querySelector(optionWrapperSelector);
  if (optionWrapper) {
    // empty() destroys the hemming-option-row (including
    // _Hem Terms Accepted input).
    while (optionWrapper.firstChild) optionWrapper.removeChild(optionWrapper.firstChild);
  }
  // Show all hidden trigger wraps inside the form.
  formEl.querySelectorAll(triggerWrapSelector).forEach((wrap) => {
    if (wrap.style.display === 'none') wrap.style.display = '';
  });
}

function handleHemLength(selectEl) {
  const selected = selectEl.options[selectEl.selectedIndex];
  if (!selected) return;
  const hemLength = selected.innerHTML;
  const form = findClosestForm(selectEl);
  if (!form) return;
  const property = form.querySelector(hemPropertySelector);
  if (property) property.value = hemLength;
}

// ─── Terms fork (modal) ───────────────────────────────────────────────────

// Module-level promise cache for the consent-modal HTML. The terms-fork
// content is page-agnostic (snippet doesn't reference `product`), so a
// single slot is enough. Cleared on error so the next attempt re-fetches.
let termsForkHtmlPromise = null;

function loadHemModalHtml() {
  if (termsForkHtmlPromise) return termsForkHtmlPromise;
  termsForkHtmlPromise = fetchSection('hemming-modal', window.location.pathname).catch((err) => {
    termsForkHtmlPromise = null;
    throw err;
  });
  return termsForkHtmlPromise;
}

export async function openHemTermsFork() {
  const wrap = document.querySelector(hemmingWrapSelector);
  if (!wrap) return;

  let modalContent;
  try {
    modalContent = await loadHemModalHtml();
  } catch (err) {
    log('[Hemming] terms fork fetch failed', err);
    throw err;
  }

  // Shopify's Section Rendering API wraps every response in
  // `<div id="shopify-section-{id}" class="shopify-section">…</div>`.
  // Unwrap so the modal body contains only the .hemming-modal markup.
  const doc = new DOMParser().parseFromString(modalContent, 'text/html');
  const sectionWrapper = doc.querySelector('#shopify-section-hemming-modal');
  const inner = (sectionWrapper ? sectionWrapper.innerHTML : modalContent).trim();
  if (!inner) {
    log('[Hemming] empty terms fork section response');
    throw new Error('[Hemming] empty section response');
  }

  notifierPush({
    type: 'modal',
    content: inner,
    name: 'hemming',
    force: true,
    disableX: true,
  });
  // Caller (add-item-form) puts the form into the loading state before
  // invoking us, to block double-submits during the fetch. Unlock now so
  // the accept-path requestSubmit can pass _handleSubmit's
  // FORM_LOADING_CLASS guard. Decline path's existing toggleFormEnabled
  // call becomes a redundant no-op.
  const formEl = document.querySelector(getAddItemFormSelector());
  if (formEl) toggleFormEnabled(formEl, true);
  ga({
    eventCategory: 'FlashMessage',
    eventAction: 'Shown',
    eventLabel: 'Hemming disclosure',
  });
}

export function closeHemTermsFork() {
  notifierDismiss();
}

function handleTermsChoice(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const button = target.closest(acceptTermsSelector + ', ' + declineTermsSelector);
  if (!button) return;
  const didAccept = button.matches(acceptTermsSelector);
  const formEl = document.querySelector(getAddItemFormSelector());
  if (!formEl) return;
  const checkbox = formEl.querySelector(validateHemCheckboxSelector);
  const hemTermsProperty = formEl.querySelector(hemTermsPropertySelector);

  // Prevent multiple submissions
  if (formEl.classList.contains('form-loading')) {
    log('Hemming form already submitting, ignoring duplicate submission');
    return false;
  }

  if (didAccept) {
    closeHemTermsFork();
    if (checkbox) checkbox.checked = true;
    if (hemTermsProperty) hemTermsProperty.value = 'Yes';
    // Use a small delay to ensure the modal is fully closed before submitting.
    setTimeout(() => {
      if (typeof formEl.requestSubmit === 'function') formEl.requestSubmit();
      else formEl.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }, 100);
    ga({
      eventCategory: 'Hemming',
      eventAction: 'Accepted',
      eventLabel: 'Hemming disclosure accepted',
    });
  } else {
    if (checkbox) checkbox.checked = false;
    closeHemTermsFork();
    closeHemPanel();
    toggleFormEnabled(formEl, true);
    ga({
      eventCategory: 'Hemming',
      eventAction: 'Declined',
      eventLabel: 'Hemming disclosure declined',
    });
  }
}

// ─── Variant-driven hem option filtering ──────────────────────────────────

const placeholderHandlerMap = new WeakMap();

export function handleSizeChange($form, variant) {
  // Accept either a jQuery collection (legacy) or a native form element.
  const formEl = unwrapJQuery($form);
  if (!formEl) return;
  const hemPanel = formEl.querySelector(hemmingOptionRowSelector);
  const hemPanelActive = !!hemPanel;
  const currentSize = variant != null ? parseInt(variant.option1) : null;

  if (currentSize !== null && variant.inventory_quantity === 0) {
    closeHemPanel();
    return;
  }

  if (!hemPanelActive) return;

  const hemSelect = hemPanel.querySelector('select');
  if (!hemSelect) return;
  const hemOptions = hemPanel.querySelectorAll('select option:not([data-extra="placeholder"])');
  const wrap = hemPanel.closest(hemmingWrapSelector);
  const isSizeLogicEnabled = wrap && wrap.getAttribute('data-size-logic') === 'true';
  const shouldDisableOptions = currentSize <= 30 && isSizeLogicEnabled;

  // Block focus events to keep the select from opening on its own (the
  // IIFE used $hemSelect.on('focusin focus', e => preventDefault())).
  // Reattaching is idempotent because we add the same listener function
  // identity each time — `addEventListener` dedupes on (type, fn, opts).
  hemSelect.addEventListener('focusin', preventFocus);
  hemSelect.addEventListener('focus', preventFocus);

  if (shouldDisableOptions) {
    hemOptions.forEach((opt) => {
      if (parseInt(opt.textContent) >= 34) opt.disabled = true;
    });
  } else {
    hemOptions.forEach((opt) => { opt.disabled = false; });
  }

  if (disallowPlaceholder) {
    // Replace any prior namespaced handler so we don't stack duplicates
    // across re-entrant size changes.
    const prior = placeholderHandlerMap.get(hemSelect);
    if (prior) hemSelect.removeEventListener('change', prior);
    const handler = () => {
      if (hemSelect.selectedIndex === 0) closeHemPanel();
    };
    hemSelect.addEventListener('change', handler);
    placeholderHandlerMap.set(hemSelect, handler);
  }

  // Also fire the legacy length handler when the user picks a length.
  hemSelect.addEventListener('change', () => handleHemLength(hemSelect));

  if (openOnAdd && hemPanel.classList.contains(appendedClass)) {
    hemPanel.classList.remove(appendedClass);
    // Prevent scroll jump on focus.
    const oldScroll = window.scrollY || window.pageYOffset;
    window.addEventListener('scroll', function once() {
      window.removeEventListener('scroll', once);
      window.scrollTo({ top: oldScroll });
    }, { once: true });
    hemSelect.focus();
    // Native <select> doesn't auto-open on focus the way selectric did;
    // showPicker() opens the OS/customizable picker. Requires a user
    // gesture, which the Add-Hemming click provides.
    if (typeof hemSelect.showPicker === 'function') {
      try { hemSelect.showPicker(); } catch (e) { /* outside gesture */ }
    }
  }
}

function preventFocus(event) { event.preventDefault(); }

// ─── Document delegates ────────────────────────────────────────────────────

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const trigger = target.closest(triggerSelector);
  if (trigger) return openHemPanel(trigger);

  const removeTrigger = target.closest(removeTriggerSelector);
  if (removeTrigger) return closeHemPanel();

  // Terms fork accept/decline routed through handleTermsChoice (it does
  // its own .closest() on the union selector).
  if (target.closest(acceptTermsSelector + ', ' + declineTermsSelector)) {
    handleTermsChoice(event);
  }
});

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  if (target.matches(hemLengthSelector)) handleHemLength(target);
});

log('[Hemming] registered');
